import { supabase } from "@/integrations/supabase/client";
import {
  getPendingSyncs,
  updateSyncStatus,
  deletePendingSync,
  deleteOfflinePhoto,
} from "./offlineDb";
import { compressImage } from "./imageCompressor";

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;

type SyncListener = (count: number) => void;
const listeners: Set<SyncListener> = new Set();

export function onSyncChange(listener: SyncListener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function notifyListeners(count: number) {
  listeners.forEach((fn) => fn(count));
}

async function uploadPhotoBlob(
  blob: Blob,
  staffUserId: string,
  orderId: string,
  folder: string
): Promise<string | null> {
  const path = `${staffUserId}/${orderId}/${folder}/${Date.now()}-${Math.random().toString(36).slice(2, 6)}.jpg`;
  const { error } = await supabase.storage
    .from("service-photos")
    .upload(path, blob, { contentType: "image/jpeg" });

  if (error) {
    console.error("[Sync] Upload error:", error.message);
    return null;
  }

  return `${SUPABASE_URL}/storage/v1/object/public/service-photos/${path}`;
}

async function processSyncItem(item: Awaited<ReturnType<typeof getPendingSyncs>>[number]) {
  await updateSyncStatus(item.id, "syncing");

  try {
    const { type, orderId, payload, photos } = item;

    // Upload any offline photos first
    const uploadedUrls: Record<string, string[]> = {
      checkin: [],
      "water-test": [],
      final: [],
    };

    for (const photo of photos) {
      const url = await uploadPhotoBlob(photo.blob, payload.staffUserId, orderId, photo.folder);
      if (url) {
        uploadedUrls[photo.folder].push(url);
        await deleteOfflinePhoto(photo.id);
      } else {
        throw new Error(`Failed to upload photo ${photo.id}`);
      }
    }

    if (type === "checkin") {
      const checkinUrl = uploadedUrls.checkin[0];
      if (!checkinUrl) throw new Error("No checkin photo uploaded");

      const { error } = await supabase
        .from("service_orders")
        .update({
          status: "in_progress",
          check_in_time: payload.checkInTime,
          initial_photo_url: checkinUrl,
        })
        .eq("id", orderId);

      if (error) throw error;
    } else if (type === "save_progress") {
      const { error } = await supabase
        .from("service_orders")
        .update(payload.formData)
        .eq("id", orderId);

      if (error) throw error;
    } else if (type === "finalize") {
      // Merge any existing final photos with newly uploaded ones
      const allFinalUrls = [
        ...(payload.existingFinalUrls || []),
        ...uploadedUrls.final,
      ];

      const updateData: any = {
        ...payload.formData,
        status: "completed",
        check_out_time: payload.checkOutTime,
        final_photo_urls: allFinalUrls,
      };

      const { error } = await supabase
        .from("service_orders")
        .update(updateData)
        .eq("id", orderId);

      if (error) throw error;

      // Handle inventory usage
      if (payload.inventoryUsage && payload.inventoryUsage.length > 0) {
        const { error: invError } = await supabase
          .from("inventory_usage")
          .insert(payload.inventoryUsage);

        if (invError) console.error("[Sync] Inventory usage error:", invError);
      }
    }

    // Success - remove from queue
    await deletePendingSync(item.id);
    console.log(`[Sync] Successfully synced: ${item.id}`);
  } catch (err: any) {
    console.error(`[Sync] Failed to sync ${item.id}:`, err);
    const newRetries = item.retries + 1;
    if (newRetries >= 5) {
      await updateSyncStatus(item.id, "failed", newRetries);
    } else {
      await updateSyncStatus(item.id, "pending", newRetries);
    }
  }
}

let syncing = false;

export async function syncPendingItems() {
  if (syncing || !navigator.onLine) return;
  syncing = true;

  try {
    const pending = await getPendingSyncs();
    if (pending.length === 0) {
      notifyListeners(0);
      return;
    }

    console.log(`[Sync] Processing ${pending.length} pending items...`);

    for (const item of pending) {
      if (!navigator.onLine) break;
      await processSyncItem(item);
    }

    const remaining = await getPendingSyncs();
    notifyListeners(remaining.length);
  } finally {
    syncing = false;
  }
}

// Auto-sync when coming back online
if (typeof window !== "undefined") {
  window.addEventListener("online", () => {
    console.log("[Sync] Back online, syncing...");
    setTimeout(() => syncPendingItems(), 1000);
  });

  // Periodic sync every 30s when online
  setInterval(() => {
    if (navigator.onLine) syncPendingItems();
  }, 30000);
}
